import React, { useState } from 'react';
import { collection, writeBatch, getDocs, doc, deleteDoc, setDoc, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { SAMPLE_PROJECT_DATA, INITIAL_SETTINGS, SAMPLE_PAGES_DATA, SAMPLE_ASSOCIATES_DATA, SAMPLE_SERVICES_DATA, SAMPLE_ABOUT_DATA, SAMPLE_CERTIFICATES_DATA, SAMPLE_TESTIMONIALS_DATA, SAMPLE_VIDEOS_DATA } from '../../constants';
import { useToast } from '../../hooks/useToast';
import ConfirmModal from '../../components/ConfirmModal';

const AdminSeederPage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const { showToast } = useToast();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalConfig, setModalConfig] = useState({ title: '', description: '', onConfirm: () => {} });

    const generateSlug = (title: string) => {
        return title
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-');
    };

    const handleSeedDatabase = async () => {
        setLoading(true);
        try {
            const projectsCollection = collection(db, 'projects');
            for (const projectData of SAMPLE_PROJECT_DATA) {
                const projectWithSlug = { ...projectData, slug: generateSlug(projectData.title) };
                await addDoc(projectsCollection, projectWithSlug);
            }
            
            const pagesCollection = collection(db, 'pages');
            for (const pageData of SAMPLE_PAGES_DATA) {
                 const pageWithSlug = { ...pageData, slug: generateSlug(pageData.title) };
                 await addDoc(pagesCollection, pageWithSlug);
            }

            const associatesCollection = collection(db, 'associates');
            for (const associateData of SAMPLE_ASSOCIATES_DATA) {
                 const associateWithSlug = { ...associateData, slug: generateSlug(associateData.name) };
                 await addDoc(associatesCollection, associateWithSlug);
            }

            const servicesCollection = collection(db, 'services');
            for (const serviceData of SAMPLE_SERVICES_DATA) {
                const serviceWithSlug = { ...serviceData, slug: generateSlug(serviceData.title) };
                await addDoc(servicesCollection, serviceWithSlug);
            }

            const certificatesCollection = collection(db, 'certificates');
            for (const certificateData of SAMPLE_CERTIFICATES_DATA) {
                await addDoc(certificatesCollection, certificateData);
            }

            const testimonialsCollection = collection(db, 'testimonials');
            for (const testimonialData of SAMPLE_TESTIMONIALS_DATA) {
                await addDoc(testimonialsCollection, testimonialData);
            }
            
            const videosCollection = collection(db, 'videos');
            for (const videoData of SAMPLE_VIDEOS_DATA) {
                await addDoc(videosCollection, videoData);
            }

            await setDoc(doc(db, 'aboutPage', 'main'), SAMPLE_ABOUT_DATA);


            showToast(`Sample data added successfully.`, { type: 'success' });
        } catch (error) {
            console.error("Error seeding database:", error);
            showToast("An error occurred while seeding. Check the console.", { type: 'error' });
        } finally {
            setLoading(false);
            setIsModalOpen(false);
        }
    };
    
    const handleDeleteCollection = async (collectionName: string) => {
        setLoading(true);
        try {
            const collectionRef = collection(db, collectionName);
            const snapshot = await getDocs(collectionRef);
            if (snapshot.empty) {
                showToast(`Collection "${collectionName}" is already empty.`, { type: 'info' });
            } else {
                const batch = writeBatch(db);
                snapshot.docs.forEach(doc => {
                    batch.delete(doc.ref);
                });
                await batch.commit();
                showToast(`All items in "${collectionName}" have been deleted.`, { type: 'success' });
            }
        } catch (error) {
            console.error(`Error deleting collection ${collectionName}:`, error);
            showToast(`Failed to delete "${collectionName}". Check console for details.`, { type: 'error' });
        } finally {
            setLoading(false);
            setIsModalOpen(false);
        }
    };
    
    const handleResetSettings = async () => {
        setLoading(true);
        try {
            await setDoc(doc(db, "settings", "global"), INITIAL_SETTINGS);
            showToast('Site settings have been reset to factory defaults.', { type: 'success' });
        } catch (error) {
            console.error("Error resetting settings:", error);
            showToast('Failed to reset settings. Check console for details.', { type: 'error' });
        } finally {
            setLoading(false);
            setIsModalOpen(false);
        }
    };

    const openModal = (type: string) => {
        let config = { title: '', description: '', onConfirm: () => {} };
        switch (type) {
            case 'seed':
                config = { title: 'Seed Database', description: 'Are you sure you want to add all sample data? This will not overwrite existing data.', onConfirm: handleSeedDatabase };
                break;
            case 'deleteProjects':
                config = { title: 'Delete All Projects', description: 'DANGER: This will permanently delete all projects from the database. This action cannot be undone.', onConfirm: () => handleDeleteCollection('projects') };
                break;
            case 'deleteArticles':
                config = { title: 'Delete All Articles', description: 'DANGER: This will permanently delete all journal articles. This action cannot be undone.', onConfirm: () => handleDeleteCollection('articles') };
                break;
            case 'deleteVideos':
                config = { title: 'Delete All Videos', description: 'DANGER: This will permanently delete all videos. This action cannot be undone.', onConfirm: () => handleDeleteCollection('videos') };
                break;
             case 'deleteTeam':
                config = { title: 'Delete All Team Members', description: 'DANGER: This will permanently delete all public team members. This action cannot be undone.', onConfirm: () => handleDeleteCollection('teamMembers') };
                break;
            case 'deleteAssociates':
                config = { title: 'Delete All Associates', description: 'DANGER: This will permanently delete all associate companies. This action cannot be undone.', onConfirm: () => handleDeleteCollection('associates') };
                break;
            case 'deleteServices':
                config = { title: 'Delete All Services', description: 'DANGER: This will permanently delete all company services. This action cannot be undone.', onConfirm: () => handleDeleteCollection('services') };
                break;
            case 'deleteCertificates':
                config = { title: 'Delete All Certificates', description: 'DANGER: This will permanently delete all certificates. This action cannot be undone.', onConfirm: () => handleDeleteCollection('certificates') };
                break;
            case 'deleteTestimonials':
                config = { title: 'Delete All Testimonials', description: 'DANGER: This will permanently delete all client testimonials. This action cannot be undone.', onConfirm: () => handleDeleteCollection('testimonials') };
                break;
            case 'resetSettings':
                 config = { title: 'Reset Site Settings', description: 'DANGER: This will reset all global site settings to their factory defaults.', onConfirm: handleResetSettings };
                break;
        }
        setModalConfig(config);
        setIsModalOpen(true);
    };

    return (
        <div>
            <ConfirmModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={modalConfig.title}
                description={modalConfig.description}
                onConfirm={modalConfig.onConfirm}
                isConfirming={loading}
            />

            <h2 className="text-3xl font-bold text-gray-800 mb-6">Database Seeder</h2>

            <div className="bg-white p-8 rounded-lg shadow-md max-w-2xl space-y-8">
                <div>
                    <h3 className="text-lg font-semibold text-gray-800">Add Sample Content</h3>
                    <p className="text-sm text-gray-600 mt-2 mb-4">
                        Populate your database with sample data for all features. This action is non-destructive and will only add new content.
                    </p>
                    <button
                        onClick={() => openModal('seed')}
                        disabled={loading}
                        className="px-6 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 transition-colors"
                    >
                        {loading ? 'Seeding...' : 'Seed All Sample Content'}
                    </button>
                </div>

                {/* Danger Zone */}
                <div className="border-2 border-red-300 bg-red-50 p-6 rounded-lg">
                     <h3 className="text-xl font-bold text-red-800">Danger Zone</h3>
                     <p className="text-sm text-red-700 mt-2 mb-6">
                        These actions are destructive and cannot be undone. Be absolutely certain before proceeding.
                    </p>
                    <div className="space-y-4">
                        <button onClick={() => openModal('deleteProjects')} disabled={loading} className="w-full px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400">Delete All Projects</button>
                        <button onClick={() => openModal('deleteArticles')} disabled={loading} className="w-full px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400">Delete All Articles</button>
                        <button onClick={() => openModal('deleteVideos')} disabled={loading} className="w-full px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400">Delete All Videos</button>
                        <button onClick={() => openModal('deleteTeam')} disabled={loading} className="w-full px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400">Delete All Public Team Members</button>
                        <button onClick={() => openModal('deleteAssociates')} disabled={loading} className="w-full px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400">Delete All Associates</button>
                        <button onClick={() => openModal('deleteServices')} disabled={loading} className="w-full px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400">Delete All Services</button>
                        <button onClick={() => openModal('deleteCertificates')} disabled={loading} className="w-full px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400">Delete All Certificates</button>
                        <button onClick={() => openModal('deleteTestimonials')} disabled={loading} className="w-full px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400">Delete All Testimonials</button>
                        <button onClick={() => openModal('resetSettings')} disabled={loading} className="w-full px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400">Reset Site Settings to Default</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminSeederPage;